import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { comboByPair } from "@/lib/data/combos";
import { services } from "@/lib/data/services";
import { cities } from "@/lib/data/cities";
import { regions, regionBySlug } from "@/lib/data/regions";
import { serviceBySlug } from "@/lib/data/services";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { ArrowUpRight } from "lucide-react";

const HERO_IMAGES = {
  "mining-security":
    "https://images.unsplash.com/photo-1655549136009-6aa6ff5de57e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBpbmR1c3RyaWFsJTIwc2l0ZXxlbnwwfHx8fDE3ODUxMDU3MjB8MA&ixlib=rb-4.1.0&q=85",
  "mobile-patrol":
    "https://images.unsplash.com/photo-1680714246510-94d1055e4a8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
  "event-security":
    "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
  "corporate-security":
    "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
};
const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1579454577332-6c8a8f99a23c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDQ2NDJ8MHwxfHNlYXJjaHw0fHx3aW5uaXBlZyUyMHNreWxpbmUlMjB3aW50ZXJ8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

export default function ServiceCityCombo() {
  const { serviceSlug, citySlug } = useParams();

  // Handle "region-*" pattern under /services/:serviceSlug/:citySlug — this
  // route also serves the /services/[service]/region-[regionSlug] URLs so
  // both combos share the same generator.
  const isRegion = typeof citySlug === "string" && citySlug.startsWith("region-");
  const regionSlug = isRegion ? citySlug.replace(/^region-/, "") : null;
  const region = regionSlug ? regionBySlug(regionSlug) : null;
  const service = serviceBySlug(serviceSlug);

  const combo = isRegion
    ? null
    : comboByPair(serviceSlug, citySlug);

  useSEO({
    title: isRegion && service && region
      ? `${service.name} in ${region.name}, Manitoba — Manitoba Security Services`
      : combo
      ? combo.title
      : "Coverage — Manitoba Security Services",
    description: isRegion && service && region
      ? `${service.name} coverage across ${region.name}. ${service.tag} Manitoba Security Services delivers one operating standard across the province.`
      : combo
      ? combo.description
      : undefined,
    keywords: isRegion && service && region
      ? withCanadaKeywords([
          `${service.name} ${region.name}`,
          `${service.name.toLowerCase()} ${region.name}`,
          `${region.name} security services`,
          `${service.name} Manitoba region`,
          ...(service.keywords || []).slice(0, 4),
          ...(region.keywords || []),
        ])
      : combo
      ? withCanadaKeywords(combo.keywords)
      : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/services/${serviceSlug}/${citySlug}`
        : `/services/${serviceSlug}/${citySlug}`,
    jsonLd: (isRegion && service && region)
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Manitoba Security Services — ${service.name} in ${region.name}`,
          description: `${service.name} coverage across ${region.name}, Manitoba.`,
          areaServed: {
            "@type": "AdministrativeArea",
            name: `${region.name}, Manitoba`,
            containedInPlace: { "@type": "Country", name: "Canada" },
          },
          makesOffer: {
            "@type": "Offer",
            itemOffered: { "@type": "Service", serviceType: service.name },
          },
        }
      : combo
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${
            typeof window !== "undefined" ? window.location.origin : ""
          }/services/${serviceSlug}/${citySlug}`,
          name: `Manitoba Security Services — ${combo.service.name} in ${combo.city.name}`,
          description: combo.description,
          areaServed: {
            "@type": "City",
            name: combo.city.name,
            containedInPlace: {
              "@type": "AdministrativeArea",
              name: "Manitoba",
              containedInPlace: { "@type": "Country", name: "Canada" },
            },
          },
          geo:
            combo.city.lat && combo.city.lng
              ? {
                  "@type": "GeoCoordinates",
                  latitude: combo.city.lat,
                  longitude: combo.city.lng,
                }
              : undefined,
          makesOffer: {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              serviceType: combo.service.name,
            },
          },
        }
      : undefined,
  });

  // Render the region-combo variant when applicable, otherwise render the city combo.
  if (isRegion) {
    if (!service || !region)
      return <Navigate to="/services" replace />;
    return <RegionComboView service={service} region={region} />;
  }

  if (!combo) return <Navigate to="/services" replace />;

  const { service: cService, city, sections } = combo;
  const hero = HERO_IMAGES[cService.slug] || DEFAULT_HERO;

  // Related: other cities for this service + other services for this city
  const otherCitiesForService = cities
    .filter((c) => c.slug !== city.slug)
    .slice(0, 12);
  const otherServicesForCity = services.filter((s) => s.slug !== cService.slug);

  return (
    <div className="bg-[#050505] pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative min-h-[80svh] w-full overflow-hidden border-b border-[#1e1e1e]">
        <img
          src={hero}
          alt={`${service.name} in ${city.name}, Manitoba`}
          className="absolute inset-0 h-full w-full object-cover grayscale contrast-125 opacity-50"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 65% 55% at 25% 45%, transparent 0%, rgba(5,5,5,0.9) 100%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-16 md:pt-24 pb-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Combo
            </p>
            <div className="mt-6 space-y-3">
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Discipline
                </p>
                <Link
                  to={`/services/${service.slug}`}
                  data-testid="combo-service-link"
                  className="font-display uppercase tracking-tight text-white text-sm md:text-base link-underline"
                >
                  {service.name}
                </Link>
              </div>
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Community
                </p>
                <Link
                  to={`/cities/${city.slug}`}
                  data-testid="combo-city-link"
                  className="font-display uppercase tracking-tight text-white text-sm md:text-base link-underline"
                >
                  {city.name}
                </Link>
              </div>
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Region
                </p>
                <p className="font-display uppercase tracking-tight text-white text-sm md:text-base">
                  {city.region}
                </p>
              </div>
              {city.lat && (
                <div>
                  <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                    Coordinates
                  </p>
                  <p className="font-display text-white text-sm">
                    {city.lat.toFixed(3)}° N / {Math.abs(city.lng).toFixed(3)}° W
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <p className="font-body uppercase tracking-[0.18em] text-[#a0a0a0] text-xs mb-4">
              Service × City
            </p>
            <RevealText
              lines={[service.name.toUpperCase(), `IN ${city.name.toUpperCase()}.`]}
              className="font-display font-black uppercase text-white tracking-tightest leading-[0.88] text-[clamp(2.5rem,8vw,7.5rem)]"
              stagger={0.1}
              delay={0.1}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-8 font-body text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed"
            >
              {combo.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-32 space-y-16 md:space-y-24">
        {sections.map((sec, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
            data-testid={`combo-section-${i}`}
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Chapter {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl md:text-3xl leading-tight">
                {sec.h}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {sec.p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Keyword tag band */}
      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-12">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            Directory tags
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {combo.keywords.map((k) => (
              <li
                key={k}
                className="font-body text-xs md:text-sm uppercase tracking-[0.14em] text-[#a0a0a0] border border-[#2a2a2a] px-3 py-1"
              >
                {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Other services in this city */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20">
        <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
          Other services in {city.name}
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
          {otherServicesForCity.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/services/${s.slug}/${city.slug}`}
                data-testid={`combo-other-service-${s.slug}`}
                className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-3 hover:border-[#FF3B30] transition-colors"
              >
                <span className="font-display uppercase tracking-tight text-white text-sm group-hover:translate-x-1 transition-transform">
                  <span className="font-editorial italic text-[#6a6a6a] mr-2">
                    {s.number}
                  </span>
                  {s.name}
                </span>
                <ArrowUpRight
                  className="text-white/40 group-hover:text-[#FF3B30]"
                  size={14}
                />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Other cities for this service */}
      <section className="border-t border-[#1e1e1e] bg-[#0b0b0b]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
            {service.name} across Manitoba
          </p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3">
            {otherCitiesForService.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/services/${service.slug}/${c.slug}`}
                  data-testid={`combo-other-city-${c.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-3 hover:border-[#FF3B30] transition-colors"
                >
                  <span className="font-display uppercase tracking-tight text-white text-sm group-hover:translate-x-1 transition-transform">
                    {c.name}
                  </span>
                  <ArrowUpRight
                    className="text-white/40 group-hover:text-[#FF3B30]"
                    size={12}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Anchor breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="border-t border-[#1e1e1e] py-6"
      >
        <ol className="mx-auto max-w-[1600px] px-6 md:px-10 flex flex-wrap gap-2 font-body text-xs uppercase tracking-[0.14em] text-[#a0a0a0]">
          <li>
            <Link to="/" className="hover:text-white">
              Index
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to="/services" className="hover:text-white">
              Services
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to={`/services/${service.slug}`} className="hover:text-white">
              {service.name}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-white">{city.name}</li>
        </ol>
      </nav>
    </div>
  );
}


// ---------- Region combo view — Service × Region ----------
function RegionComboView({ service, region }) {
  const regionCitySlugs = region.cities || [];

  return (
    <div className="bg-[#050505] pt-40 md:pt-48">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Service × Region
          </p>
          <div className="mt-6 space-y-3">
            <div>
              <p className="font-body uppercase tracking-[0.14em] text-[#a0a0a0] text-[10px]">
                Discipline
              </p>
              <Link
                to={`/services/${service.slug}`}
                className="font-display uppercase tracking-tight text-white text-sm link-underline"
              >
                {service.name}
              </Link>
            </div>
            <div>
              <p className="font-body uppercase tracking-[0.14em] text-[#a0a0a0] text-[10px]">
                Region
              </p>
              <Link
                to={`/regions/${region.slug}`}
                className="font-display uppercase tracking-tight text-white text-sm link-underline"
              >
                {region.name}
              </Link>
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <RevealText
            lines={[service.name.toUpperCase(), `IN ${region.name.toUpperCase()}.`]}
            className="font-display font-black uppercase text-white tracking-tightest leading-[0.9] text-[clamp(2.5rem,7vw,6rem)]"
            stagger={0.1}
            delay={0.1}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 font-body text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed"
          >
            {service.name} coverage across {region.name}, Manitoba.{" "}
            {service.tag} Manitoba Security Services delivers one operating
            standard across the province — {region.hero}
          </motion.p>
        </div>
      </section>

      {/* Cities within region */}
      {regionCitySlugs.length > 0 && (
        <section className="border-t border-[#1e1e1e] bg-[#0b0b0b] py-16 md:py-24">
          <div className="mx-auto max-w-[1600px] px-6 md:px-10">
            <p className="font-editorial italic text-[#FF3B30] text-sm mb-6">
              {service.name} across {region.name}
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3">
              {regionCitySlugs.map((cs) => (
                <li key={cs}>
                  <Link
                    to={`/services/${service.slug}/${cs}`}
                    className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-3 hover:border-[#FF3B30] transition-colors"
                  >
                    <span className="font-display uppercase tracking-tight text-white text-sm group-hover:translate-x-1 transition-transform">
                      {cs.replace(/-/g, " ")}
                    </span>
                    <ArrowUpRight className="text-white/40 group-hover:text-[#FF3B30]" size={12} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="border-t border-[#1e1e1e] py-6">
        <ol className="mx-auto max-w-[1600px] px-6 md:px-10 flex flex-wrap gap-2 font-body text-xs uppercase tracking-[0.14em] text-[#a0a0a0]">
          <li>
            <Link to="/" className="hover:text-white">Index</Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to="/services" className="hover:text-white">Services</Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to={`/services/${service.slug}`} className="hover:text-white">
              {service.name}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-white">{region.name}</li>
        </ol>
      </nav>
    </div>
  );
}